import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchSuggestField from './SearchSuggestField';

const defaultGetOptionValue = (option) => String(option?.id ?? option?.value ?? '');
const defaultGetOptionLabel = (option) => String(option?.label ?? option?.nombre ?? option?.name ?? option?.descripcion ?? '');

export default function SearchableSelectField({
  className,
  options = [],
  value,
  onChange,
  getOptionValue = defaultGetOptionValue,
  getOptionLabel = defaultGetOptionLabel,
  getOptionSearch,
  renderItem,
  placeholder = 'Buscar...',
  emptyMessage = 'Sin resultados',
  disabled = false,
  size = 'sm',
  showAllOnFocus = true,
  closeOnSelect = true,
  invalid = false,
  dropUp = false,
  saltarAlSiguiente = true,
  // Búsqueda server-side (ver CLAUDE.md, "para buscar en una tabla grande,
  // buscar en el servidor"). Sin `onSearch`, se sigue filtrando en memoria
  // sobre `options` — el comportamiento de siempre, sin romper nada. Con
  // `onSearch`, `options` ya viene filtrado por el padre (vía useBusquedaRemota
  // u otro hook equivalente) y este componente no vuelve a filtrar.
  onSearch,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  searchError = false,
  // Muestra el botón "×" para vaciar la selección (ver SearchSuggestField). No
  // se pasa en un combo obligatorio de un formulario.
  clearable = false,
}) {
  const selectedOption = useMemo(
    () => (options || []).find((option) => String(getOptionValue(option)) === String(value ?? '')),
    [options, value, getOptionValue]
  );

  const [query, setQuery] = useState('');
  // Evita que el efecto pise el texto cuando el usuario inició el cambio (tipeo o borrado)
  const skipSyncRef = useRef(false);

  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    if (!selectedOption) {
      if (!value) {
        setQuery('');
      }
      return;
    }
    setQuery(getOptionLabel(selectedOption));
  }, [selectedOption, value, getOptionLabel]);

  return (
    <SearchSuggestField
      className={className}
      value={query}
      onValueChange={(nextValue) => {
        skipSyncRef.current = true;
        setQuery(nextValue);
        if (!String(nextValue || '').trim()) {
          onChange?.('');
        }
      }}
      items={options}
      selectedKey={value}
      onSelectItem={(option) => {
        onChange?.(String(getOptionValue(option)));
        setQuery(getOptionLabel(option));
      }}
      getItemKey={(option) => String(getOptionValue(option))}
      getSearchText={(option) => {
        if (typeof getOptionSearch === 'function') {
          return String(getOptionSearch(option) || '');
        }
        return getOptionLabel(option);
      }}
      renderItem={renderItem || ((option) => <span>{getOptionLabel(option)}</span>)}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      disabled={disabled}
      size={size}
      showAllOnFocus={showAllOnFocus}
      closeOnSelect={closeOnSelect}
      invalid={invalid}
      dropUp={dropUp}
      saltarAlSiguiente={saltarAlSiguiente}
      onSearch={onSearch}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
      loadingMore={loadingMore}
      searchError={searchError}
      onClear={clearable ? () => {
        skipSyncRef.current = true;
        setQuery('');
        onChange?.('');
      } : undefined}
    />
  );
}
